import { useEffect, useState } from "react";
import api, { fmtMoney, fmtDate } from "@/lib/api";
import Topbar from "@/components/layout/Topbar";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ArrowsClockwise, Scales, Trash, Warning, CheckCircle, Receipt } from "@phosphor-icons/react";
import { toast } from "sonner";

const Card = ({ icon: Icon, title, subtitle, right, children, testid }) => (
  <section data-testid={testid} className="bg-white border border-[#E5E5E5] rounded-sm">
    <header className="flex items-center justify-between gap-3 px-5 py-4 border-b border-[#E5E5E5]">
      <div className="flex items-center gap-3">
        <span className="w-8 h-8 rounded-sm bg-[#921A0E]/10 text-[#921A0E] flex items-center justify-center"><Icon size={16} weight="bold" /></span>
        <div>
          <h2 className="font-heading text-[15px] font-semibold text-[#1a1a1a]">{title}</h2>
          <p className="text-[11px] text-neutral-500">{subtitle}</p>
        </div>
      </div>
      <div className="flex items-center gap-2">{right}</div>
    </header>
    {children}
  </section>
);

const Dif = ({ v, moneda }) => (
  <span className={`font-mono ${Math.abs(v) < 0.01 ? "text-neutral-400" : v > 0 ? "num-neg" : "num-pos"}`}>{Math.abs(v) < 0.01 ? "—" : fmtMoney(v, moneda)}</span>
);

function SaldosSection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fixing, setFixing] = useState(false);
  const [confirm, setConfirm] = useState(false);

  const revisar = async () => {
    setLoading(true);
    try { const { data } = await api.get("/mantenimiento/saldos-diferencias"); setData(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo revisar"); }
    finally { setLoading(false); }
  };
  const corregir = async () => {
    setFixing(true);
    try {
      const { data: r } = await api.post("/mantenimiento/saldos-recalcular");
      toast.success(`${r.corregidos} saldo(s) corregido(s)`);
      setConfirm(false);
      await revisar();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo corregir"); }
    finally { setFixing(false); }
  };
  useEffect(() => { revisar(); }, []);

  const difs = data?.diferencias || [];
  return (
    <Card icon={Scales} title="Saldos de cuentas corrientes vs. mayor" testid="saldos-section"
      subtitle="Compara el saldo guardado de cada cliente/proveedor con la suma de sus comprobantes y cobros/pagos."
      right={<>
        <Button data-testid="saldos-revisar-btn" size="sm" variant="outline" className="rounded-sm h-8 text-[11px]" onClick={revisar} disabled={loading}>
          <ArrowsClockwise size={13} className={`mr-1 ${loading ? "animate-spin" : ""}`} /> Revisar
        </Button>
        <Button data-testid="saldos-corregir-btn" size="sm" className="rounded-sm h-8 text-[11px] bg-[#921A0E] hover:bg-[#7a1509]" onClick={() => setConfirm(true)} disabled={!difs.length || fixing}>
          <CheckCircle size={13} className="mr-1" /> Corregir todos ({difs.length})
        </Button>
      </>}
    >
      {data && difs.length === 0 && (
        <div data-testid="saldos-ok" className="px-5 py-8 text-center text-[13px] text-emerald-700 flex items-center justify-center gap-2">
          <CheckCircle size={16} weight="fill" /> {data.revisados} cuentas revisadas · todos los saldos coinciden con el mayor
        </div>
      )}
      {difs.length > 0 && (
        <table className="w-full text-[12px]">
          <thead className="text-[10px] uppercase font-mono text-[#666] bg-neutral-50">
            <tr>
              <th className="px-4 py-2 text-left w-24">Tipo</th>
              <th className="px-4 py-2 text-left">Nombre</th>
              <th className="px-4 py-2 text-right">Saldo guardado</th>
              <th className="px-4 py-2 text-right">Según mayor</th>
              <th className="px-4 py-2 text-right">Diferencia</th>
              <th className="px-4 py-2 text-right">Dif. USD</th>
            </tr>
          </thead>
          <tbody>
            {difs.map((d) => (
              <tr key={d.id} data-testid={`saldo-dif-${d.id}`} className="border-t border-[#F0F0F0] hover:bg-neutral-50">
                <td className="px-4 py-2 font-mono text-[10px] uppercase tracking-widest text-neutral-500">{d.tipo}</td>
                <td className="px-4 py-2">{d.nombre}</td>
                <td className="px-4 py-2 text-right font-mono">{fmtMoney(d.saldo_ars)}</td>
                <td className="px-4 py-2 text-right font-mono font-semibold">{fmtMoney(d.mayor_ars)}</td>
                <td className="px-4 py-2 text-right"><Dif v={d.dif_ars} /></td>
                <td className="px-4 py-2 text-right"><Dif v={d.dif_usd} moneda="USD" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Corregir {difs.length} saldo(s)?</AlertDialogTitle>
            <AlertDialogDescription>
              El saldo guardado de cada cuenta listada se reemplaza por el calculado desde el mayor (comprobantes vigentes − cobros/pagos). No se modifica ningún comprobante ni movimiento.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={fixing}>Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="saldos-corregir-confirm" onClick={corregir} disabled={fixing} className="bg-[#921A0E] hover:bg-[#7a1509]">
              {fixing ? "Corrigiendo…" : "Corregir"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

function CobrosNcSection() {
  const [items, setItems] = useState(null);
  const [del, setDel] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/mantenimiento/cobros-nc"); setItems(data || []); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo cargar"); }
  };
  useEffect(() => { load(); }, []);

  const eliminar = async () => {
    if (!del) return;
    setBusy(true);
    try {
      await api.delete(`/cobros-pagos/${del.id}`);
      toast.success(`${del.tipo === "cobro" ? "Cobro" : "Pago"} eliminado · saldo de ${del.contraparte_nombre} y caja revertidos`);
      setDel(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo eliminar"); }
    finally { setBusy(false); }
  };

  return (
    <Card icon={Receipt} title="Cobros/pagos generados por Notas de Crédito" testid="cobros-nc-section"
      subtitle="Al emitir una NC con medio de pago se creaba un cobro/pago automático que no corresponde a un movimiento real de caja. Eliminalos de a uno: se revierte el saldo de la cta. cte. y de la caja."
      right={<Button data-testid="cobros-nc-reload-btn" size="sm" variant="outline" className="rounded-sm h-8 text-[11px]" onClick={load}><ArrowsClockwise size={13} className="mr-1" /> Actualizar</Button>}
    >
      {items && items.length === 0 && (
        <div data-testid="cobros-nc-vacio" className="px-5 py-8 text-center text-[13px] text-emerald-700 flex items-center justify-center gap-2">
          <CheckCircle size={16} weight="fill" /> No hay cobros ni pagos ligados a Notas de Crédito
        </div>
      )}
      {items && items.length > 0 && (
        <table className="w-full text-[12px]">
          <thead className="text-[10px] uppercase font-mono text-[#666] bg-neutral-50">
            <tr>
              <th className="px-4 py-2 text-left w-24">Fecha</th>
              <th className="px-4 py-2 text-left w-16">Tipo</th>
              <th className="px-4 py-2 text-left">Contraparte · Caja</th>
              <th className="px-4 py-2 text-left">Concepto / comprobantes</th>
              <th className="px-4 py-2 text-right">Monto</th>
              <th className="px-4 py-2 w-10"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.id} data-testid={`cobro-nc-${c.id}`} className="border-t border-[#F0F0F0] hover:bg-neutral-50 group">
                <td className="px-4 py-2 font-mono">{fmtDate(c.fecha)}</td>
                <td className={`px-4 py-2 font-mono text-[10px] uppercase tracking-widest ${c.tipo === "cobro" ? "num-pos" : "num-neg"}`}>{c.tipo}</td>
                <td className="px-4 py-2">
                  <div>{c.contraparte_nombre}</div>
                  <div className="text-[10px] text-neutral-500">{c.caja_nombre}{c.conciliado && <span className="ml-2 text-amber-700 inline-flex items-center gap-0.5"><Warning size={10} /> conciliado</span>}</div>
                </td>
                <td className="px-4 py-2">
                  <div className="truncate max-w-[420px]" title={c.concepto}>{c.concepto}</div>
                  <div className="text-[10px] font-mono text-neutral-500">{c.comprobantes.join(" · ")}</div>
                </td>
                <td className="px-4 py-2 text-right font-mono">{fmtMoney(c.monto, c.moneda)}</td>
                <td className="px-2 py-2 text-center">
                  <button data-testid={`cobro-nc-del-${c.id}`} onClick={() => setDel(c)} className="p-1.5 rounded-sm text-[#999] hover:text-red-600 hover:bg-red-50" title="Eliminar (revierte saldo y caja)">
                    <Trash size={13} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <AlertDialog open={!!del} onOpenChange={(v) => !v && setDel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar este {del?.tipo}?</AlertDialogTitle>
            <AlertDialogDescription>
              {del && <>{del.contraparte_nombre} · {fmtMoney(del.monto, del.moneda)} · {del.caja_nombre}. Se revierte el saldo de la cuenta corriente y el movimiento de caja asociado.{del.conciliado && " Está conciliado con un movimiento del banco: la conciliación se deshace."}</>}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="cobro-nc-del-confirm" onClick={eliminar} disabled={busy} className="bg-red-600 hover:bg-red-700">
              {busy ? "Eliminando…" : "Eliminar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

export default function MantenimientoPage() {
  return (
    <>
      <Topbar title="Mantenimiento de datos" subtitle="Controles de consistencia · sólo administradores" />
      <div className="p-6 space-y-6 max-w-6xl" data-testid="mantenimiento-page">
        <SaldosSection />
        <CobrosNcSection />
      </div>
    </>
  );
}
